import React, { useState, useEffect, useCallback } from 'react';
import { Badge } from '../components/ui/badge';
import { Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { screenerAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import LegalDisclaimer from '../components/LegalDisclaimer';
import AddToJournalDialog from '../components/AddToJournalDialog';
import PresetGrid from './screener/PresetGrid';
import AdvancedFiltersCard from './screener/AdvancedFiltersCard';
import ResultsTable from './screener/ResultsTable';

const EMPTY_FILTERS = {
  minPrice: '',
  maxPrice: '',
  minVolume: '',
  sector: '',
  maxPe: '',
};

const Screener = () => {
  const { isPro } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [activePreset, setActivePreset] = useState('momentum');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [journalTarget, setJournalTarget] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const loadScreenerData = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const params = { preset: activePreset, limit: 25 };
      if (filters.minPrice) params.min_price = Number(filters.minPrice);
      if (filters.maxPrice) params.max_price = Number(filters.maxPrice);
      if (filters.minVolume) params.min_volume = Number(filters.minVolume);
      if (filters.sector) params.sector = filters.sector;
      if (filters.maxPe) params.max_pe = Number(filters.maxPe);

      const data = await screenerAPI.run(params);
      setResults(data?.results || []);
    } catch (e) {
      const status = e?.response?.status;
      const msg =
        status === 503
          ? 'Market data temporarily rate-limited by Yahoo Finance. Retry in ~60s.'
          : 'Failed to load screener results';
      setErrorMsg(msg);
      setResults([]);
      console.warn('Screener error', e);
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [activePreset, filters, toast]);

  useEffect(() => {
    loadScreenerData();
    // Re-run on preset change only — filters apply on Run button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePreset]);

  const handleAddJournal = (row) => {
    if (!isPro) {
      toast({
        title: 'Pro required',
        description: 'Upgrade to log trades.',
        variant: 'destructive',
      });
      return;
    }
    setJournalTarget({ symbol: row.symbol, price: row.price });
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Stock Screener</h1>
            <p className="text-gray-500">Live yfinance data across 70+ liquid US tickers</p>
          </div>
          {!isPro && (
            <Badge className="bg-purple-600 text-white" data-testid="screener-pro-badge">
              <Lock size={14} className="mr-1" />
              Advanced Filters: Pro
            </Badge>
          )}
        </div>

        <PresetGrid activePreset={activePreset} onPresetChange={setActivePreset} />

        {isPro && (
          <AdvancedFiltersCard
            filters={filters}
            onChange={setFilters}
            onApply={loadScreenerData}
            loading={loading}
          />
        )}

        <ResultsTable
          results={results}
          loading={loading}
          errorMsg={errorMsg}
          isPro={isPro}
          onResearch={(row) => navigate(`/research?symbol=${row.symbol}`)}
          onAddJournal={handleAddJournal}
          onRefresh={loadScreenerData}
        />

        <LegalDisclaimer variant="compact" />
      </div>
      <AddToJournalDialog
        open={!!journalTarget}
        onOpenChange={(o) => {
          if (!o) setJournalTarget(null);
        }}
        symbol={journalTarget?.symbol}
        defaultEntryPrice={journalTarget?.price}
        defaultSide="buy"
        source="screener"
      />
    </div>
  );
};

export default Screener;
